import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Chip,
  Checkbox,
  Typography,
  Button,
  Divider,
} from '@mui/material';
import dayjs from 'dayjs';
import { budgetLines, fmtMoney, payeeLabel } from '../../utils/format';
import { isRowFinalized } from '../../utils/rows';
import { EXPENSE_STATUS_META, STATUS_ROW_TINT } from '../../constants/status';

import { getBankLabel } from '../../../../../constants/thaiBanks';
// มุมมองมือถือของ 1 แถวในตาราง — ใช้แทน <ExpenseRow> (ที่เป็น <tr>) เมื่อ isMobile
// โครงสร้างข้อมูล/เงื่อนไขการแสดงปุ่ม (finalized / ตัดสินใจแล้ว / รอตัดสินใจ) ยึดตาม ExpenseRow.js เป๊ะ
// ต่างแค่ layout: การ์ดแนวตั้ง + ปุ่ม action เต็มความกว้าง กดง่ายด้วยนิ้ว — flag หลัก/รองมาจาก mode
export default function ExpenseCard({
  mode,
  row,
  index,
  selected,
  onToggleSelect,
  onOpenDetail,
  onAction,
}) {
  const { secondary } = mode;
  const { codeLine, nameLine, remaining } = budgetLines(row);
  const primaryStatusMeta = mode.statusMeta[row?.[mode.statusField] || 'PENDING'];
  const expenseStatusMeta =
    EXPENSE_STATUS_META[row?.status] ||
    (row?.status ? { label: row.status, color: 'default' } : null);
  const secondaryStatusMeta =
    secondary.statusMeta[row?.[secondary.statusField] || 'PENDING'];

  const finalized = isRowFinalized(row);
  const primaryStatus = row?.[mode.statusField] || 'PENDING';
  const hasDecision = primaryStatus !== 'PENDING';
  const actorLabel = mode.actorLabel[primaryStatus];
  // เวลาที่ status เปลี่ยนเป็น SUCCESS จริง — หา log entry ล่าสุดที่ status ตรงกับสถานะปัจจุบัน (mirror
  // ExpenseRow.js's latestApprovalLog) ตอน finalized แล้ว rawExpenseStatus คือ 'SUCCESS' พอดี entry นี้
  // ถูก push จาก payment.service.js completePayment() ตอนกด "จ่ายเงิน"
  const rawExpenseStatus = row?.status || 'PENDING';
  const latestApprovalLog =
    [...(row?.approval_logs || [])].reverse().find((log) => log.status === rawExpenseStatus) ||
    null;

  const chipSx = {
    height: 22,
    fontSize: '0.68rem',
    fontWeight: 700,
    '& .MuiChip-label': { px: 0.9 },
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        bgcolor: selected
          ? 'action.selected'
          : STATUS_ROW_TINT[primaryStatusMeta?.color] || 'background.paper',
        borderColor: selected ? 'primary.main' : 'divider',
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* หัวการ์ด: ลำดับ + รหัส + วันที่ / checkbox เลือก (เฉพาะแถวที่ยังเลือกได้) */}
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="baseline">
              <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', fontWeight: 700 }}>
                #{index + 1}
              </Typography>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }} noWrap>
                {row?.code || '-'}
              </Typography>
            </Stack>
            {row?.date && (
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                วันที่ขอ {dayjs(row.date).format('DD/MM/YYYY')}
              </Typography>
            )}
          </Box>
          {!finalized && !hasDecision && (
            <Checkbox
              size="small"
              checked={selected}
              onChange={onToggleSelect}
              sx={{ p: 0.5, mt: -0.5, mr: -0.5 }}
            />
          )}
        </Stack>

        {/* chip สถานะ: flag หลัก / รายการเบิก / flag รอง */}
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          {primaryStatusMeta && (
            <Chip
              size="small"
              variant="filled"
              color={primaryStatusMeta.color}
              label={primaryStatusMeta.label}
              sx={chipSx}
            />
          )}
          {expenseStatusMeta && (
            <Chip
              size="small"
              variant="outlined"
              color={expenseStatusMeta.color}
              label={expenseStatusMeta.label}
              sx={chipSx}
            />
          )}
          {secondaryStatusMeta && (
            <Chip
              size="small"
              variant="filled"
              color={secondaryStatusMeta.color}
              label={secondaryStatusMeta.label}
              sx={chipSx}
            />
          )}
        </Stack>

        {/* ยอดเบิก — เด่นสุดในการ์ด */}
        <Box sx={{ mt: 1.25 }}>
          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
            ยอดเบิก
          </Typography>
          <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, lineHeight: 1.2 }}>
            {fmtMoney(row.net_price || row.price)} บาท
          </Typography>
        </Box>

        {/* บัญชีผู้รับเงิน */}
        <Box sx={{ mt: 1 }}>
          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
            บัญชีผู้รับเงิน
          </Typography>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
            {payeeLabel(row)}
          </Typography>
          {(row?.payee?.bank || row?.payee?.account_number) && (
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
              {[getBankLabel(row?.payee?.bank), row?.payee?.account_number].filter(Boolean).join(' ')}
            </Typography>
          )}
        </Box>

        {/* งาน-รหัสงบ */}
        <Box sx={{ mt: 1 }}>
          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
            งาน-รหัสงบ
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{codeLine}</Typography>
          {nameLine && (
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
              {nameLine}
            </Typography>
          )}
          {remaining != null && (
            <Typography
              sx={{
                fontSize: '0.72rem',
                fontWeight: 600,
                color: remaining < 0 ? 'error.main' : 'success.main',
              }}
            >
              เหลือ {fmtMoney(remaining)} บาท
            </Typography>
          )}
        </Box>

        {/* รายละเอียดรายการ — แตะเพื่อเปิด modal รายละเอียดเต็ม */}
        <Box
          sx={{ mt: 1, cursor: 'pointer' }}
          onClick={onOpenDetail}
        >
          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
            รายละเอียด <span style={{ opacity: 0.6 }}>(แตะเพื่อดูเพิ่ม)</span>
          </Typography>
          <Typography sx={{ fontSize: '0.82rem', wordBreak: 'break-word' }}>
            {row?.name || '-'}
          </Typography>
        </Box>

        {/* ผู้ตัดสินใจ + หมายเหตุ (ถ้าตัดสินใจไปแล้ว) */}
        {hasDecision && (
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
              {actorLabel || 'ดำเนินการโดย'}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
              {row?.[mode.actorNameField] || '-'}
              {row?.[mode.actorAtField] &&
                ` • ${dayjs(row[mode.actorAtField]).format('DD/MM/YYYY HH:mm')}`}
            </Typography>
            {row?.[mode.noteField] && (
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
                หมายเหตุ: {row[mode.noteField]}
              </Typography>
            )}
          </Box>
        )}

        <Divider sx={{ my: 1.25 }} />

        {/* ปุ่ม action — เต็มความกว้าง กดด้วยนิ้วง่าย */}
        {finalized ? (
          <Box sx={{ textAlign: 'center', py: 0.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontStyle: 'italic', display: 'block' }}
            >
              จ่ายแล้ว
            </Typography>
            {latestApprovalLog?.acted_at && (
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                {dayjs(latestApprovalLog.acted_at).format('DD/MM/YYYY HH:mm')}
              </Typography>
            )}
          </Box>
        ) : hasDecision ? (
          <Button
            fullWidth
            size="medium"
            color="inherit"
            variant="outlined"
            onClick={() => onAction('RESET')}
          >
            {mode.resetCardLabel}
          </Button>
        ) : (
          <Stack direction="row" spacing={1}>
            <Button
              fullWidth
              size="medium"
              color="success"
              variant="contained"
              onClick={() => onAction(mode.positiveAction.code)}
            >
              {mode.positiveAction.label}
            </Button>
            <Button
              fullWidth
              size="medium"
              color="error"
              variant="outlined"
              onClick={() => onAction(mode.negativeAction.code)}
            >
              {mode.negativeAction.label}
            </Button>
            <Button
              fullWidth
              size="medium"
              color="warning"
              variant="outlined"
              onClick={() => onAction('HOLD')}
            >
              รอก่อน
            </Button>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
